import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Backdrop,
  CircularProgress,
  Snackbar
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import { useMutation } from "@tanstack/react-query";
import { FormikValues, useFormik } from "formik";
import * as Yup from "yup";

import { useStyles } from "./styles";
import StorageProvider from "../../../framework/src/StorageProvider";
import { createCustomPricingBump } from "./queries";

interface NewCustomPricingBumpProps {
  isOpen: boolean;
  handleClose: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => void;
}

function NewCustomPricingBump({
  isOpen,
  handleClose,
  refetch
}: NewCustomPricingBumpProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const classes = useStyles();

  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const fetchToken = async () => {
      const storedToken = await StorageProvider.get("authToken");
      if (storedToken) {
        setToken(storedToken);
      }
    };

    fetchToken();
  }, []);

  const {
    isPending,
    mutate,
    failureCount,
    failureReason,
    isError,
    isSuccess,
    reset,
  } = useMutation({
    mutationFn: async (form: FormikValues) => {
      if (!token) {
        return;
      }
      createCustomPricingBump(form, token);
    },
    onSuccess: () => {
      formik.resetForm();
    },
    onSettled: () => {
      refetch();
    }
  });

  const formik = useFormik({
    initialValues: {
      bumpDescription: "",
      bumpAmount: "",
      bumpFor: "",
      bumpName: "",
      bumpType: 0
    },
    validationSchema: Yup.object({
      bumpDescription: Yup.string().required("Required"),
      bumpAmount: Yup.number()
        .typeError("Must be a number")
        .required("Required")
        .test(
          "max-decimal-places",
          "Prices can have a maximum of 2 decimal places.",
          value => {
            if (value === null || value === undefined) {
              return true; // Allow null or undefined values if not required
            }
            // Convert the number to a string to apply regex
            const stringValue = String(value);
            // Regex to match a number with up to 2 decimal places
            // ^\\d+ matches one or more digits at the beginning
            // (\\.\\d{1,2})? matches an optional decimal point followed by 1 or 2 digits
            // $ ensures no other characters follow
            return /^(\d+(\.\d{1,2})?)$/.test(stringValue);
          }
        ),
      bumpFor: Yup.string().required("Required"),
      bumpName: Yup.string().required("Required"),
      bumpType: Yup.number()
        .required("Required")
        .typeError("Invalid option")
        .min(0, "Must be one of the options above")
        .max(1, "Must be one of the options above")
    }),
    onSubmit: (values: FormikValues) => {
      mutate(values);
    },
  });

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={handleClose}
      scroll="body"
      aria-labelledby="responsive-dialog-title"
    >
      <Backdrop className={classes.backdrop} open={isPending}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={!isPending && isError}
        autoHideDuration={12000}
        onClose={() => reset()}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error" elevation={6} variant="filled">
          <Typography variant="h3">
            Could not save your Pricing Bump!
          </Typography>
          <Typography>{`${failureCount} attempts made`}</Typography>
          <Typography>{`Details: ${failureReason}`}</Typography>
        </Alert>
      </Snackbar>
      <Snackbar
        open={!isPending && isSuccess}
        autoHideDuration={12000}
        onClose={() => reset()}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" elevation={6} variant="filled">
          <Typography variant="h3">
            New Pricing Bump created successfully!
          </Typography>
          <Button
            color="inherit"
            size="medium"
            variant="text"
            onClick={_ => window.location.replace(window.location.href)}
          >
            Reload
          </Button>
        </Alert>
      </Snackbar>
      <DialogTitle disableTypography>
        <Typography className={classes.dialogTitle}>
          Add Pricing Bump
        </Typography>
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            {/* Bump Description */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                className={classes.formControl}
              >
                <TextField
                  label="If the client"
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  {...formik.getFieldProps("bumpDescription")}
                  error={
                    formik.touched.bumpDescription &&
                    Boolean(formik.errors.bumpDescription)
                  }
                  helperText={
                    formik.touched.bumpDescription &&
                    formik.errors.bumpDescription
                  }
                />
              </FormControl>
            </Grid>
            {/* Bump Amount */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                className={classes.formControl}
              >
                <TextField
                  id="outlined-number"
                  label="increase their total fee by ($)"
                  {...formik.getFieldProps("bumpAmount")}
                  error={
                    formik.touched.bumpAmount &&
                    Boolean(formik.errors.bumpAmount)
                  }
                  helperText={
                    formik.touched.bumpAmount && formik.errors.bumpAmount
                  }
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </FormControl>
            </Grid>
            {/* Bump For */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                className={classes.formControl}
              >
                <TextField
                  label="for each"
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  {...formik.getFieldProps("bumpFor")}
                  error={
                    formik.touched.bumpFor && Boolean(formik.errors.bumpFor)
                  }
                  helperText={formik.touched.bumpFor && formik.errors.bumpFor}
                />
              </FormControl>
            </Grid>
            {/* Bump Name */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                className={classes.formControl}
              >
                <TextField
                  label="Bump Package Name"
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  {...formik.getFieldProps("bumpName")}
                  error={
                    formik.touched.bumpName && Boolean(formik.errors.bumpName)
                  }
                  helperText={formik.touched.bumpName && formik.errors.bumpName}
                />
              </FormControl>
            </Grid>
            {/* Bump Type */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                className={classes.formControl}
                error={
                  formik.touched.bumpType && Boolean(formik.errors.bumpType)
                }
              >
                <InputLabel id="bump-type-select-label">Bump Type</InputLabel>
                <Select
                  labelId="bump-type-select-label"
                  id="bump-type-select"
                  {...formik.getFieldProps("bumpType")}
                  value={formik.values.bumpType}
                  label="Bump Type"
                >
                  <MenuItem value={0}>Individual</MenuItem>
                  <MenuItem value={1}>Business</MenuItem>
                </Select>
                <FormHelperText error>
                  {formik.touched.bumpType && formik.errors.bumpType}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => handleClose(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button variant="contained" type="submit" color="primary">
            Submit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default NewCustomPricingBump;
